import { useRef, useState, useEffect } from "react";
import { Move } from "lucide-react";

const PROJECTS = [
  {
    label: "Paradise Valley Estate",
    detail: "House Wash · 14,200 sq ft",
    img: "https://images.unsplash.com/photo-1570630856429-32cf3feef67c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    beforeFilter:
      "saturate(0.45) brightness(0.78) contrast(0.92) sepia(0.18)",
  },
  {
    label: "DC Ranch Driveway",
    detail: "Concrete Restoration",
    img: "https://static.prod-images.emergentagent.com/jobs/1236a11f-ca38-4921-83f3-221e6d2210b6/images/eeca7660a0ba5df0d4c4fe7f702890060d484fb92e9a9724e8d46f4921587335.png",
    beforeFilter:
      "saturate(0.4) brightness(0.7) contrast(0.95) sepia(0.22)",
  },
  {
    label: "Silverleaf Pavers",
    detail: "Travertine + Re-sand",
    img: "https://images.unsplash.com/photo-1768215882809-99534393395d?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    beforeFilter:
      "saturate(0.5) brightness(0.72) contrast(0.95) sepia(0.2)",
  },
];

export default function BeforeAfter() {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);
  const [project, setProject] = useState(0);
  const current = PROJECTS[project];

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const p = Math.max(2, Math.min(98, (x / rect.width) * 100));
      setPos(p);
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging]);

  return (
    <section
      id="showcase"
      data-testid="before-after-section"
      className="relative bg-[#0a0a0c] py-24 sm:py-32 lg:py-40 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
          <p className="font-mono-small text-[#E5D0A1] mb-6">— The Sonoran Difference</p>
          <h2 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl tracking-[-0.035em] leading-[1.04]">
            Drag the line.
            <br />
            <span className="font-editorial italic text-white/60 font-normal">
              See the surface return.
            </span>
          </h2>
        </div>

        <div
          ref={ref}
          data-testid="before-after-slider"
          className="ba-slider relative w-full aspect-[16/10] sm:aspect-[16/9] overflow-hidden border border-white/10 select-none cursor-ew-resize"
          onMouseDown={(e) => {
            setDragging(true);
            const rect = ref.current.getBoundingClientRect();
            setPos(Math.max(2, Math.min(98, ((e.clientX - rect.left) / rect.width) * 100)));
          }}
          onTouchStart={(e) => {
            setDragging(true);
            const rect = ref.current.getBoundingClientRect();
            setPos(
              Math.max(
                2,
                Math.min(98, ((e.touches[0].clientX - rect.left) / rect.width) * 100)
              )
            );
          }}
        >
          {/* AFTER (full color) */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${current.img})` }}
          />
          {/* BEFORE (same image, filtered to feel grimy) */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${current.img})`,
              filter: current.beforeFilter,
              clipPath: `inset(0 ${100 - pos}% 0 0)`,
            }}
          />
          {/* Subtle vignette */}
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/30 via-transparent to-transparent" />

          {/* Labels */}
          <div className="absolute top-5 left-5 sm:top-6 sm:left-6 font-mono-small text-white bg-black/50 backdrop-blur-md border border-white/15 px-3 py-1.5">
            Before
          </div>
          <div className="absolute top-5 right-5 sm:top-6 sm:right-6 font-mono-small text-[#09090B] bg-[#E5D0A1] px-3 py-1.5">
            After
          </div>

          {/* Handle */}
          <div className="ba-handle" style={{ left: `${pos}%` }} />
          <div
            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none"
            style={{ left: `${pos}%` }}
          >
            <div className="ba-knob">
              <Move className="w-5 h-5" strokeWidth={1.5} />
            </div>
          </div>
        </div>

        {/* Project switcher */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-t-0 border-white/10">
          {PROJECTS.map((p, i) => (
            <button
              key={p.label}
              data-testid={`project-tab-${i}`}
              onClick={() => {
                setProject(i);
                setPos(50);
              }}
              className={`text-left p-6 transition-colors duration-500 ${
                project === i
                  ? "bg-white text-black"
                  : "bg-[#0d0d10] text-white/70 hover:text-white hover:bg-[#111114]"
              }`}
            >
              <p
                className={`font-mono-small mb-2 ${
                  project === i ? "text-[#09090B]/70" : "text-[#E5D0A1]"
                }`}
              >
                Project · 0{i + 1}
              </p>
              <p className="font-display text-xl tracking-tight">{p.label}</p>
              <p
                className={`text-sm font-body mt-1 ${
                  project === i ? "text-[#09090B]/70" : "text-white/50"
                }`}
              >
                {p.detail}
              </p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
